/**
 * These utility functions are used to prepare the marking context for the marking process.
 */
import { UserFragment } from "@/generated/graphql";

import { LoadGroupsPayload, MarkingGroup, MarkingGroupMember } from "./types";

/** Group ID of students without marking group */
export const DEFAULT_GROUP_ID = "default-group-id";

/**
 * Initialise Marking Group state for marking.
 *
 * @param payload - Information needed to load groups into the marking slice.
 * @returns Groups state for display that is used in markingTool slice
 */
export function initMarkingGroups(payload: LoadGroupsPayload): MarkingGroup[] {
  // list of Student IDs that has a work outcome
  const studentWithOutcomeIds = payload.workOutcomes.map(
    (workOutcome) => workOutcome.studentId
  );

  const groupWithOutcomeIds = payload.workOutcomes.map(
    (workOutcome) => workOutcome.groupId
  );

  // list of Student IDs that belongs to a group
  const groupedStudentIds = payload.groups.flatMap((group) =>
    group.members.map((member) => member.id)
  );

  const ungroupedStudents = payload.allStudents
    .filter(
      (student) =>
        // Student is not in a Group but has a WorkOutcome
        studentWithOutcomeIds.includes(student.id) &&
        !groupedStudentIds.includes(student.id)
    )
    .map((student) => ({
      ...student,
      displayName: selectStudentDisplayName(
        student.name,
        student.anonymousIndex,
        payload.anonymousMarkingEnabled
      ),
    }));

  const processedGroups = payload.groups
    .map((group) => {
      return {
        ...group,
        members: mapMarkingGroupMembers(
          group.members,
          payload.allStudents,
          payload.anonymousMarkingEnabled
        ),
      };
    })
    .filter(
      (group) =>
        group.members.length > 0 || groupWithOutcomeIds.includes(group.id)
    );

  const groups =
    ungroupedStudents.length > 0
      ? [
          ...processedGroups,
          {
            id: DEFAULT_GROUP_ID,
            name: "Ungrouped",
            members: ungroupedStudents,
            markerId: null,
            code: null,
            questionId: null,
            marker: null,
          },
        ]
      : processedGroups;

  return groups;
}

function mapMarkingGroupMembers(
  users: UserFragment[],
  allStudents: Array<UserFragment & { anonymousIndex: number | null }>,
  anonymousMarkingEnabled: boolean
): MarkingGroupMember[] {
  return users.map((user) => {
    const studentInfo = allStudents.find((s) => s.id === user.id);
    if (studentInfo && studentInfo.anonymousIndex !== null) {
      return {
        id: user.id,
        displayName: selectStudentDisplayName(
          user.name,
          studentInfo.anonymousIndex,
          anonymousMarkingEnabled
        ),
      };
    }
    return { id: user.id, displayName: user.name };
  });
}

// Use `name` or anonymised name if anonymous marking is enabled
function selectStudentDisplayName(
  name: string,
  anonymousIndex: number | null,
  anonymousMarkingEnabled: boolean
): string {
  return anonymousMarkingEnabled && anonymousIndex !== null
    ? `Student ${anonymousIndex}`
    : name;
}

/**
 * Get the Student's Group ID based on the provided groups and student ID.
 * If the student is not part of any group, it returns null.
 */
export function getStudentGroupId({
  groups,
  studentId,
}: {
  groups: MarkingGroup[];
  studentId: string;
}): string | null {
  return (
    groups?.find((g) => g.members.some((m) => m.id === studentId))?.id ?? null
  );
}
